// PRE RMA CSB — Synchronisation automatique et discrete (points 28-30).
// Decisions validees (Phase 5) :
//   - conflits = SERVEUR GAGNE, jamais d'ecrasement silencieux ;
//   - une modification (UPDATE) suit exactement le meme principe hors-ligne
//     qu'une creation : ecriture locale immediate, puis synchro automatique.

import { api, getSession } from '../api/client.ts';
import {
  listerEnAttente, marquerSynchronise, marquerRefuse, marquerConflit, sauvegarderLocalement,
  mettreEnCacheConsultations, obtenirEnAttente, supprimerLocal, supprimerDuCache, listerParPeriode, sauvegarderLotLocalement,
  purgerConsultationsPeriode, purgerCachePeriode,
} from './db.ts';
import type { LocalConsultation } from './db.ts';
import type { ConsultationInput } from '../../../shared/business-logic/validation.ts';
import { nettoyerConsultation } from '../../../shared/business-logic/nettoyage.ts';
import { normaliserConsultationInput } from '../../../shared/business-logic/validation.ts';
import { trouverOrphelinsSynchronises, bilanReparation } from '../../../shared/business-logic/synchro.ts';
import type { BilanReparation } from '../../../shared/business-logic/synchro.ts';

export type EtatSynchro = 'IDLE' | 'SYNCING' | 'ERROR';
type Ecouteur = (etat: EtatSynchro, nbEnAttente: number) => void;

const ecouteurs = new Set<Ecouteur>();
let enCours = false;
let debutEnCours = 0;
// Garde-fou : si une synchro est « en cours » depuis plus que ça (onglet gelé, IndexedDB bloquée…), on autorise une nouvelle tentative
// au lieu de rester bloqué pour toujours.
const DUREE_MAX_SYNCHRO_MS = 5 * 60_000;
let relanceProgrammee: ReturnType<typeof setTimeout> | null = null;
// Temps maximal pendant lequel l'écran attend la réponse du serveur après « Supprimer tout » avant de rendre la main à l'utilisateur.
const DELAI_ATTENTE_ORDRE_MS = 4_000;

/**
 * Erreur à NE PAS traiter comme définitive : réseau, délai dépassé, serveur indisponible (5xx/408), limite de débit (429) ou session
 * expirée (401, déjà renouvelée automatiquement par api/client.ts avant d'arriver ici). Ces erreurs-là ARRÊTENT la passe (inutile
 * d'insister sur un réseau coupé).
 * ⚠ 403 n'en fait PLUS partie : c'est un refus métier (ex. limite du compte d'essai, rôle insuffisant) qui ne doit pas bloquer
 * toute la file → l'élément est marqué « Refusé / À vérifier » et on passe au suivant.
 */
function estTransitoire(err: any): boolean {
  const st = err?.status;
  // 500 = plantage applicatif sur CETTE saisie → refus définitif (voir estRefusDefinitif). Les autres 5xx (502/503/504, 52x Cloudflare,
  // démarrage à froid de Render…) restent des pannes d'infrastructure : on ne marque rien comme refusé, on réessaiera.
  return !st || st === 401 || st === 408 || st === 429 || (st >= 500 && st !== 500);
}

/** Pannes qui justifient d'ARRÊTER la passe : réseau coupé, délai dépassé, limite de débit (429), session expirée (401). Un 5xx isolé n'en est pas une. */
function estPanneGlobale(err: any): boolean {
  const st = err?.status;
  // Sans code HTTP : réseau coupé / délai dépassé (api/client.ts pose `reseau`) = panne globale ; toute autre erreur locale inattendue
  // (IndexedDB, bug d'affichage…) ne concerne que CET élément : on le laisse en attente et on passe au suivant.
  if (!st) return err?.reseau === true || (typeof navigator !== 'undefined' && !navigator.onLine);
  return st === 401 || st === 408 || st === 429;
}
/** Nombre de 5xx consécutifs (502/503/504…) au-delà duquel la passe s'arrête. */
const MAX_ERREURS_SERVEUR_DE_SUITE = 3;

/** Refus définitif : erreur de validation / refus métier (4xx) ou crash 500 → l'élément est marqué « Refusé » et la file continue. */
function estRefusDefinitif(err: any): boolean {
  const st = err?.status;
  return !!st && !estTransitoire(err) && (st === 500 || (st >= 400 && st < 500));
}

function motifRefus(err: any): string {
  if (err?.details?.length) return err.details.join(' / ');
  if (err?.status === 500) return "Erreur interne du serveur : cette saisie est mise de côté (rien n'est perdu). Corrigez-la ou utilisez « Forcer la synchronisation ».";
  return err?.message ?? 'Saisie refusée par le serveur.';
}

export function ecouterEtatSynchro(fn: Ecouteur): () => void {
  ecouteurs.add(fn);
  return () => ecouteurs.delete(fn);
}

async function notifier(etat: EtatSynchro) {
  const nb = (await listerEnAttente()).filter((i) => i.syncStatus === 'PENDING').length;
  ecouteurs.forEach((fn) => fn(etat, nb));
}

/** Enregistre une NOUVELLE consultation localement, puis tente une synchro immediate si en ligne. */
export async function enregistrerConsultation(
  periodeId: string,
  csbId: string,
  payload: ConsultationInput
): Promise<{ localId: string }> {
  const localId = crypto.randomUUID();
  const item: LocalConsultation = {
    localId, operation: 'CREATE', periodeId, csbId,
    payload: { ...normaliserConsultationInput(payload), periodeId, localId },
    syncStatus: 'PENDING', createdAt: Date.now(), updatedAt: Date.now(),
  };
  await sauvegarderLocalement(item);
  await notifier('IDLE');
  if (navigator.onLine) traiterFile();
  return { localId };
}

/**
 * IMPORT en masse (ancien outil) : toutes les consultations sont écrites d'un coup dans IndexedDB (en attente d'envoi), donc visibles
 * immédiatement, sans connexion. Elles partent vers le serveur ensuite, comme une saisie normale (file d'attente + synchro automatique).
 */
export async function importerConsultations(periodeId: string, csbId: string, lot: ConsultationInput[]): Promise<number> {
  const maintenant = Date.now();
  const items: LocalConsultation[] = lot.map((payload, i) => {
    const localId = crypto.randomUUID();
    return {
      localId, operation: 'CREATE', periodeId, csbId, payload: { ...normaliserConsultationInput(payload), periodeId, localId },
      syncStatus: 'PENDING', createdAt: maintenant + i, updatedAt: maintenant + i, // +i : l'ordre du fichier est conservé dans la liste
    };
  });
  await sauvegarderLotLocalement(items);
  await notifier('IDLE');
  if (navigator.onLine) traiterFile();
  return items.length;
}

/**
 * Enregistre une MODIFICATION d'une consultation déjà synchronisée, avec
 * exactement le même principe hors-ligne que la création (décision validée).
 * `baseVersion` = la version connue au moment où l'utilisateur a ouvert le
 * formulaire d'édition — nécessaire pour la détection de conflit (point 30).
 */
export async function enregistrerModification(
  periodeId: string,
  csbId: string,
  consultationId: string,
  baseVersion: number,
  payload: ConsultationInput
): Promise<{ localId: string }> {
  const localId = crypto.randomUUID();
  const item: LocalConsultation = {
    localId, operation: 'UPDATE', periodeId, csbId, consultationId, baseVersion,
    payload: { ...normaliserConsultationInput(payload), periodeId },
    syncStatus: 'PENDING', createdAt: Date.now(), updatedAt: Date.now(),
  };
  await sauvegarderLocalement(item);
  await notifier('IDLE');
  if (navigator.onLine) traiterFile();
  return { localId };
}

/**
 * Corrige un patient saisi sur cet appareil et pas encore envoyé : la création en attente est remplacée par la version corrigée
 * (et remise en file si elle était en erreur de validation). Si l'envoi a eu lieu entre-temps, bascule sur une vraie modification.
 */
export async function modifierPatientLocal(localId: string, periodeId: string, csbId: string, payload: ConsultationInput): Promise<void> {
  const item = await obtenirEnAttente(localId);
  if (!item) throw new Error('Ce patient a été supprimé ou envoyé : actualisez la liste.');
  if (item.syncStatus === 'SYNCED' && item.serverId) {
    await enregistrerModification(periodeId, csbId, item.serverId, item.serverVersion ?? 1, payload);
    return;
  }
  await sauvegarderLocalement({ ...item, payload: { ...normaliserConsultationInput(payload), periodeId, localId }, syncStatus: 'PENDING', erreur: undefined, updatedAt: Date.now() });
  await notifier('IDLE');
  if (navigator.onLine) traiterFile();
}

/**
 * SUPPRIME un patient (confirmation déjà obtenue auprès de l'utilisateur par l'écran).
 *  - patient jamais envoyé au serveur (saisi hors connexion) : simple retrait de la file, rien à annuler côté serveur ;
 *  - patient déjà enregistré : opération DELETE placée dans la file (fonctionne aussi hors connexion), retiré tout de suite
 *    de l'affichage et du cache ; envoyée dès que possible. Les modifications en attente sur ce patient sont abandonnées.
 * Suppression LOGIQUE côté serveur (voir DELETE /consultations/:id) : journalisée, N° RCE réutilisable.
 */
export async function supprimerPatient(
  patient: { id?: string; localId?: string; version?: number; data: ConsultationInput },
  periodeId: string,
  csbId: string
): Promise<'RETIRE_DE_LA_FILE' | 'SUPPRESSION_PLANIFIEE'> {
  if (!patient.id) {
    if (!patient.localId) throw new Error('Patient introuvable.');
    const item = await obtenirEnAttente(patient.localId);
    if (item && item.syncStatus !== 'SYNCED') { await supprimerLocal(patient.localId); await notifier('IDLE'); return 'RETIRE_DE_LA_FILE'; }
    if (item?.serverId) return supprimerPatient({ ...patient, id: item.serverId, version: item.serverVersion }, periodeId, csbId);
    // La création vient peut-être de partir : traiterFile détecte l'élément retiré et planifie la suppression (voir plus bas).
    await supprimerLocal(patient.localId); await notifier('IDLE');
    return 'RETIRE_DE_LA_FILE';
  }
  if (patient.version === undefined) throw new Error('Version du patient inconnue : actualisez la liste puis réessayez.');
  for (const it of await listerParPeriode(periodeId)) {
    if (it.consultationId === patient.id && it.operation === 'UPDATE' && it.syncStatus !== 'SYNCED') await supprimerLocal(it.localId);
  }
  const localId = crypto.randomUUID();
  const item: LocalConsultation = {
    localId, operation: 'DELETE', periodeId, csbId, consultationId: patient.id, baseVersion: patient.version,
    payload: { numeroRceBrut: patient.data.numeroRceBrut, ageBrut: patient.data.ageBrut, genre: patient.data.genre },
    syncStatus: 'PENDING', createdAt: Date.now(), updatedAt: Date.now(),
  };
  await sauvegarderLocalement(item);
  await supprimerDuCache(patient.id);
  await notifier('IDLE');
  if (navigator.onLine) traiterFile();
  return 'SUPPRESSION_PLANIFIEE';
}

/**
 * « SUPPRIMER TOUT » (consultations uniquement) : vide TOUT le mois — état local, file d'attente IndexedDB (y compris les saisies bloquées en
 * attente / en erreur / en conflit et les éléments déjà SYNCED) et cache de lecture — puis envoie UN ordre au serveur
 * (DELETE /consultations?periodeId=…, suppression logique). Hors connexion, l'ordre reste en file (DELETE_ALL) et part au retour du réseau.
 * Retourne 'FAITE' si le serveur a confirmé, 'PLANIFIEE' sinon (l'écran est de toute façon déjà vidé).
 */
export async function supprimerToutesConsultations(periodeId: string, csbId: string): Promise<{ retiresLocalement: number; serveur: 'FAITE' | 'PLANIFIEE' }> {
  // 1) Purge locale d'abord : l'écran est vide tout de suite et plus rien ne sera (ré)envoyé. Une création déjà partie vers le serveur
  //    pendant ce temps est rattrapée par traiterFile (élément retiré → suppression planifiée).
  const retiresLocalement = await purgerConsultationsPeriode(periodeId);
  // 2) Ordre de suppression côté serveur, durable (survit à une coupure ou à un rechargement).
  const ordre: LocalConsultation = {
    localId: crypto.randomUUID(), operation: 'DELETE_ALL', periodeId, csbId, payload: {},
    syncStatus: 'PENDING', createdAt: Date.now(), updatedAt: Date.now(),
  };
  await sauvegarderLocalement(ordre);
  await notifier('IDLE');
  // L'interface ne doit JAMAIS rester bloquée par le réseau (2G, coupure, session expirée) : on laisse la synchro tourner en arrière-plan
  // et on n'attend sa réponse que DELAI_ATTENTE_ORDRE_MS au plus. Passé ce délai, la purge locale est déjà faite et l'ordre reste en file.
  if (navigator.onLine) {
    const envoi = traiterFile().catch(() => { /* erreur réseau/auth : l'ordre reste PENDING et repartira tout seul */ });
    await Promise.race([envoi, new Promise<void>((r) => setTimeout(r, DELAI_ATTENTE_ORDRE_MS))]);
  }
  const restant = await obtenirEnAttente(ordre.localId);
  return { retiresLocalement, serveur: restant ? 'PLANIFIEE' : 'FAITE' };
}

/**
 * Traite la file d'attente — STRICTEMENT SÉQUENTIEL, UNE requête à la fois (jamais de lot, jamais de Promise.all) : sur un réseau
 * 2G/3G, des envois groupés ou parallèles saturent la bande passante et échouent. Un rejet métier du serveur (400/403/404/409/422/423…)
 * ne bloque JAMAIS la file : l'élément est marqué « Refusé / À vérifier » (syncStatus ERROR + message), mis de côté, et on envoie
 * immédiatement le suivant. Un 5xx isolé (502/503/504…) laisse l'élément en attente et passe AUSSI au suivant ; seules les vraies pannes
 * (réseau coupé, délai dépassé, 429, session expirée, ou 3 erreurs serveur de suite) arrêtent la passe.
 * Idempotent et re-entrant-safe via le flag
 * `enCours` (evite deux synchronisations paralleles, ex: reconnexion +
 * bouton manuel actionnes en meme temps).
 * On repasse tant qu'il reste des éléments en attente jamais tentés pendant cette exécution
 * (suppressions ajoutées pendant le traitement, suppression planifiée après un envoi, etc.).
 */
export async function traiterFile(options: { reessayerRefuses?: boolean } = {}): Promise<void> {
  if (!navigator.onLine) return;
  if (enCours && Date.now() - debutEnCours < DUREE_MAX_SYNCHRO_MS) return;
  enCours = true;
  debutEnCours = Date.now();
  let interrompue = false; // vrai si la passe a été arrêtée par une panne réseau / limite / session expirée
  let serveurEnPanne = false; // vrai si au moins un élément a été laissé en attente à cause d'un 5xx (502/503/504…) pendant cette passe
  let erreursServeurDeSuite = 0; // 5xx consécutifs : le serveur est manifestement hors service au-delà de MAX_ERREURS_SERVEUR_DE_SUITE
  await notifier('SYNCING');
  try {
    const tentes = new Set<string>();
    // Éléments marqués « Erreur » uniquement à cause d'une session expirée (ancienne version) : ils n'ont rien à corriger → on les remet en file.
    // « Forcer la synchronisation » : les saisies refusées (ex. crash 500 corrigé depuis) sont retentées UNE fois pendant cette passe.
    for (const i of await listerEnAttente()) {
      if (i.syncStatus === 'ERROR' && (options.reessayerRefuses || /session invalide|session expir|authentification requise/i.test(i.erreur ?? ''))) {
        await sauvegarderLocalement({ ...i, syncStatus: 'PENDING', erreur: undefined, updatedAt: Date.now() });
      }
    }
    for (;;) {
      // Les ordres « Supprimer tout » partent EN PREMIER (tri stable) : les saisies faites après passent ensuite et ne sont pas effacées.
      const enAttente = (await listerEnAttente()).filter((i) => i.syncStatus === 'PENDING' && !tentes.has(i.localId))
        .sort((a, b) => Number(b.operation === 'DELETE_ALL') - Number(a.operation === 'DELETE_ALL'));
      if (!enAttente.length || interrompue) break;
      for (const item of enAttente) {
        if (interrompue) break;
        tentes.add(item.localId);
        // Le serveur écrit csb_id d'après le JETON : une saisie d'un CSB ne doit jamais partir avec le jeton d'un autre compte (ex. Admin
        // après « Se reconnecter »). Elle reste en attente, intacte, jusqu'à la reconnexion de son propre compte.
        if ((item.csbId || null) !== (getSession().csbId || null)) continue;
        debutEnCours = Date.now(); // la synchro progresse : le garde-fou repart de zéro
        try {
          if (item.operation === 'CREATE') {
            // Nettoyage juste avant l'appel API : trim de toutes les chaînes (N° RCE, âge, diagnostics…), les null significatifs restent null.
            const res = await api.post<{ id: string; version: number }>('/consultations', nettoyerConsultation(item.payload));
            if (!(await obtenirEnAttente(item.localId))) {
              // Le patient a été supprimé par l'utilisateur PENDANT l'envoi : il existe maintenant côté serveur → on planifie sa suppression.
              await sauvegarderLocalement({
                localId: crypto.randomUUID(), operation: 'DELETE', periodeId: item.periodeId, csbId: item.csbId, consultationId: res.id, baseVersion: res.version,
                payload: { numeroRceBrut: item.payload.numeroRceBrut, ageBrut: item.payload.ageBrut, genre: item.payload.genre },
                syncStatus: 'PENDING', createdAt: Date.now(), updatedAt: Date.now(),
              });
              continue;
            }
            await marquerSynchronise(item.localId, res.id, res.version);
          } else if (item.operation === 'DELETE_ALL') {
            // Suppression logique de tout le mois côté serveur ; 404 (période introuvable) = rien à supprimer = succès.
            try { await api.delete(`/consultations?periodeId=${item.periodeId}`); }
            catch (e: any) { if (e.status !== 404) throw e; }
            await supprimerLocal(item.localId); // confirmé par le serveur : on peut purger l'ordre
            await purgerCachePeriode(item.periodeId); // le cache a pu être re-rempli entre-temps par un rafraîchissement
          } else if (item.operation === 'DELETE') {
            // Suppression logique côté serveur ; 404 = déjà supprimé (rejeu, autre appareil) = succès.
            try { await api.delete(`/consultations/${item.consultationId}?baseVersion=${item.baseVersion}`); }
            catch (e: any) { if (e.status !== 404) throw e; }
            await supprimerLocal(item.localId); // confirmé par le serveur : on peut purger
            // La création / modification « ✓ Synchronisé » du même patient doit disparaître aussi, sinon la liste le ré-affiche (patient « fantôme »).
            await retirerSynchronisesDe(item.periodeId, item.consultationId);
            if (item.consultationId) await supprimerDuCache(item.consultationId);
          } else {
            // UPDATE : verrou optimiste côté serveur (point 30). 409 = SERVEUR GAGNE, jamais de rejeu automatique.
            const res = await api.patch<{ id: string; version: number }>(
              `/consultations/${item.consultationId}`,
              { ...nettoyerConsultation(item.payload), baseVersion: item.baseVersion }
            );
            await marquerSynchronise(item.localId, res.id, res.version);
          }
          erreursServeurDeSuite = 0; // une opération a abouti : le serveur répond
        } catch (err: any) {
          if (err.status === 410 && item.operation === 'CREATE') {
            // Le serveur sait que cette saisie a déjà été supprimée : rien à envoyer, on la retire de la file (pas de « fantôme »).
            await supprimerLocal(item.localId);
          } else if (err.status === 404 && item.operation === 'UPDATE' && (!item.csbId || item.csbId === getSession().csbId)) {
            // La fiche n'existe plus sous cet identifiant côté serveur (supprimée, restaurée ailleurs, saisie en attente…) : la correction
            // de l'utilisateur n'est PAS perdue — elle repart comme NOUVELLE saisie, que l'on peut ensuite modifier ou supprimer normalement.
            const nouveauLocalId = crypto.randomUUID();
            await sauvegarderLocalement({
              localId: nouveauLocalId, operation: 'CREATE', periodeId: item.periodeId, csbId: item.csbId,
              payload: { ...item.payload, periodeId: item.periodeId, localId: nouveauLocalId },
              syncStatus: 'PENDING', createdAt: item.createdAt, updatedAt: Date.now(),
            });
            await retirerSynchronisesDe(item.periodeId, item.consultationId);
            await supprimerLocal(item.localId);
            if (item.consultationId) await supprimerDuCache(item.consultationId);
            programmerRelance(300);
          } else if (err.status === 409 && item.operation === 'DELETE') {
            await marquerConflit(
              item.localId,
              err.versionServeur,
              "Suppression non appliquée : ce patient a été modifié entre-temps par un autre appareil ou utilisateur. " +
                'Actualisez la liste, vérifiez la fiche, puis supprimez-la de nouveau si nécessaire.'
            );
          } else if (err.status === 409 && item.operation === 'UPDATE') {
            await marquerConflit(
              item.localId,
              err.versionServeur,
              "Cette consultation a été modifiée entre-temps par un autre appareil ou utilisateur. " +
                'La version du serveur a été conservée ; rouvrez la consultation pour repartir de la version à jour si votre correction est toujours nécessaire.'
            );
          } else if (estTransitoire(err) && estPanneGlobale(err)) {
            // Réseau coupé/lent, délai dépassé, limite de débit (429) ou session expirée (401) : l'élément RESTE en attente
            // et on ARRÊTE la passe (inutile d'attendre un délai par élément restant). Reprise automatique : 429 → après le délai
            // demandé par le serveur ; sinon minuteur de 60 s, retour du réseau, ou bouton « Forcer la synchronisation ».
            interrompue = true;
            if (err.status === 429) programmerRelance((err.retryAfter ?? 30) * 1000 + 500);
          } else if (estTransitoire(err)) {
            // 502/503/504/52x : l'élément reste en attente (réessayé à la passe suivante) mais NE FIGE PAS la file : on passe immédiatement au suivant.
            // On ne s'arrête que si le serveur échoue MAX_ERREURS_SERVEUR_DE_SUITE fois de suite (il est alors réellement hors service).
            serveurEnPanne = true;
            erreursServeurDeSuite += 1;
            if (erreursServeurDeSuite >= MAX_ERREURS_SERVEUR_DE_SUITE) interrompue = true;
          } else if (estRefusDefinitif(err)) {
            // Refus définitif : erreur de validation (400/422), refus métier (403/404/409/423…) ou crash serveur (500). Pas de nouvelle tentative
            // automatique. L'élément est mis à jour dans IndexedDB avec la mention « Refusé » (statut ERROR = « ⛔ Refusé / À vérifier »),
            // puis la boucle `for` passe IMMÉDIATEMENT à l'élément suivant : une saisie défectueuse ne bloque jamais les autres.
            await marquerRefuse(item.localId, motifRefus(err), err.status).catch(() => { /* IndexedDB indisponible : l'élément restera PENDING et sera réessayé */ });
          }
        }
      }
    }
  } finally {
    enCours = false;
    await notifier(navigator.onLine && !interrompue && !serveurEnPanne ? 'IDLE' : 'ERROR');
  }
}

/** Retire de la file locale les éléments « Synchronisé » (création / modification déjà envoyées) d'un patient supprimé. */
async function retirerSynchronisesDe(periodeId: string, consultationId?: string): Promise<void> {
  if (!consultationId) return;
  for (const it of await listerParPeriode(periodeId)) {
    if (it.syncStatus === 'SYNCED' && it.operation !== 'DELETE' && it.operation !== 'DELETE_ALL'
        && (it.serverId === consultationId || it.consultationId === consultationId)) {
      await supprimerLocal(it.localId);
    }
  }
}

/**
 * Patients « ✓ Synchronisé » dont l'identifiant n'existe PLUS dans la liste que le serveur vient de renvoyer pour la période (supprimés,
 * validés sous un autre identifiant…). Ils n'ont rien à envoyer : on les retire de la file locale, sinon ils restent affichés alors que
 * toute modification ou suppression donne « 404 Consultation introuvable ». `avant` = instant du début de la lecture serveur : un élément
 * synchronisé PENDANT cette lecture n'est jamais touché.
 */
export async function nettoyerOrphelinsSynchronises(periodeId: string, idsServeur: Set<string>, avant: number): Promise<number> {
  const ids = trouverOrphelinsSynchronises(await listerParPeriode(periodeId), idsServeur, avant); // logique pure (testée)
  for (const localId of ids) await supprimerLocal(localId);
  return ids.length;
}

function programmerRelance(delaiMs: number) {
  if (relanceProgrammee) clearTimeout(relanceProgrammee);
  relanceProgrammee = setTimeout(() => { relanceProgrammee = null; void traiterFile(); }, delaiMs);
}

/**
 * Récupère la liste des consultations d'une période depuis le serveur et
 * met à jour le cache local — permet un affichage hors-ligne ultérieur
 * (Saisie Unique : liste à modifier ; RMA/Tableau de suivi/Historique via
 * le service worker, voir docs/PHASE6_PWA.md).
 */
export async function rafraichirCacheConsultations(periodeId: string): Promise<Array<Record<string, unknown>>> {
  return (await lireListeServeur(periodeId)).rows;
}

/** Lit la liste serveur de la période, met à jour le cache et retire les « fantômes » (patients « Synchronisé » que le serveur ne connaît plus). */
async function lireListeServeur(periodeId: string): Promise<{ rows: Array<Record<string, unknown>>; orphelins: number }> {
  const debutLecture = Date.now();
  const rows = await api.get<Array<Record<string, unknown> & { id: string }>>(`/consultations?periodeId=${periodeId}`);
  await mettreEnCacheConsultations(periodeId, rows);
  const orphelins = await nettoyerOrphelinsSynchronises(periodeId, new Set(rows.map((r) => r.id)), debutLecture).catch(() => 0);
  return { rows, orphelins };
}

export interface RapportReparation extends BilanReparation {
  etat: 'OK' | 'HORS_LIGNE' | 'SESSION_EXPIREE' | 'SERVEUR_INDISPONIBLE';
  message: string;
  orphelinsRetires: number;
  patientsServeur: number | null;
}

/** Attend (au plus `maxMs`) qu'une synchronisation déjà en cours se termine, pour ne pas la doubler. */
async function attendreFinSynchro(maxMs: number): Promise<void> {
  const fin = Date.now() + maxMs;
  while (enCours && Date.now() < fin) await new Promise((r) => setTimeout(r, 300));
}

/**
 * « RÉPARER LA SYNCHRONISATION » — remplace l'ancien bouton « Vider les saisies locales du mois ». NE SUPPRIME JAMAIS une saisie, ni sur
 * l'appareil ni sur le serveur. Étapes : (a) connexion et session ; (b)(c) envoi de tout ce qui attend, y compris les saisies refusées
 * qui peuvent être retentées ; (d) rechargement de la liste du mois depuis le serveur ; (e) retrait des « fantômes » ; (f) rapport.
 */
export async function reparerSynchronisation(periodeId: string): Promise<RapportReparation> {
  const vide: BilanReparation = { envoyes: 0, repares: 0, bloques: [] };
  if (!navigator.onLine) {
    return { ...vide, etat: 'HORS_LIGNE', orphelinsRetires: 0, patientsServeur: null, message: "Pas de connexion : rien n'a été modifié. Vos saisies restent sur cet appareil et partiront dès le retour du réseau." };
  }
  if (!getSession().token) {
    return { ...vide, etat: 'SESSION_EXPIREE', orphelinsRetires: 0, patientsServeur: null, message: 'Session expirée : reconnectez-vous, puis relancez la réparation. Vos saisies sont conservées.' };
  }
  await attendreFinSynchro(20_000);
  const avant = await listerEnAttente();
  await traiterFile({ reessayerRefuses: true }); // (b) + (c) : envoi séquentiel, refusés retentés une fois
  let rows: Array<Record<string, unknown>> | null = null;
  let orphelins = 0;
  let etat: RapportReparation['etat'] = 'OK';
  try {
    const lecture = await lireListeServeur(periodeId); // (d) + (e)
    rows = lecture.rows; orphelins = lecture.orphelins;
  } catch (e: any) {
    etat = e?.status === 401 ? 'SESSION_EXPIREE' : 'SERVEUR_INDISPONIBLE';
  }
  const bilan = bilanReparation(avant, await listerEnAttente());
  const message = etat === 'SESSION_EXPIREE' ? 'Session expirée : reconnectez-vous, puis relancez la réparation. Vos saisies sont conservées.'
    : etat === 'SERVEUR_INDISPONIBLE' ? "Le serveur ne répond pas pour le moment : rien n'a été supprimé, réessayez un peu plus tard."
    : bilan.bloques.length ? `${bilan.bloques.length} saisie(s) restent à vérifier (voir ci-dessous). Elles sont conservées.`
    : 'Tout est synchronisé.';
  return { ...bilan, etat, message, orphelinsRetires: orphelins, patientsServeur: rows ? rows.length : null };
}

/** Remet en file UNE saisie refusée (statut « Refusé / À vérifier ») et lance l'envoi. Les conflits se règlent en rouvrant la fiche. */
export async function renvoyerElement(localId: string): Promise<void> {
  const item = await obtenirEnAttente(localId);
  if (!item || item.syncStatus !== 'ERROR') return;
  await sauvegarderLocalement({ ...item, syncStatus: 'PENDING', erreur: undefined, updatedAt: Date.now() });
  await notifier('IDLE');
  if (navigator.onLine) void traiterFile();
}

// Reprise automatique des que la connexion revient (point 28, "Etape 4").
window.addEventListener('online', () => { traiterFile(); });
// Retente periodiquement (filet de securite si l'evenement 'online' est manque).
setInterval(() => { if (navigator.onLine) traiterFile(); }, 60_000);
// Retente aussi quand l'utilisateur revient sur l'application (onglet/appli remis au premier plan).
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible' && navigator.onLine) void traiterFile(); });
